import client from '../../../config/api';
import { USERS_ADD_FAVORITE } from '../../users/actionTypes';

const LINKED_COUNTS_IDS_CHUNK_SIZE = 200;

export default {
  async FETCH_STANDARD_TASKS({ commit }, options = {}) {
    const params = {
      ...(options?.includeLinkedWiCounts
        ? { includeLinkedWiCounts: true }
        : {}),
      ...(options?.deleted ? { deleted: true } : {}),
      ...(options?.status ? { status: options.status } : {}),
      ...((
        Array.isArray(options?.statusFilter)
          ? options.statusFilter.length > 0
          : Boolean(options?.statusFilter)
      )
        ? { statusFilter: options.statusFilter }
        : {}),
      ...(options?.search ? { search: options.search } : {}),
      ...(Number.isFinite(Number(options?.limit)) && Number(options.limit) > 0
        ? { limit: Number(options.limit) }
        : {}),
      ...(options?.startKey ? { startKey: options.startKey } : {}),
    };

    const response = await client.get('/standard-tasks', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    const data = response?.data;

    if (Array.isArray(data)) {
      if (!options?.skipCommit) {
        commit('SET_STANDARD_TASKS', data);
      }
      return data;
    }

    const pagedItems = Array.isArray(data?.items) ? data.items : [];
    if (!options?.skipCommit) {
      commit('SET_STANDARD_TASKS', pagedItems);
    }

    if (options?.returnPage) {
      return {
        items: pagedItems,
        count: Number(data?.count || 0),
        lastEvaluatedKey: data?.lastEvaluatedKey || null,
      };
    }

    return pagedItems;
  },

  async FETCH_STANDARD_TASK_LINKED_COUNTS(_, taskIds = []) {
    const ids = Array.isArray(taskIds)
      ? [
          ...new Set(
            taskIds.map((id) => String(id || '').trim()).filter(Boolean)
          ),
        ]
      : [];

    if (ids.length < 1) {
      return {};
    }

    const chunkIdsList = [];
    for (
      let index = 0;
      index < ids.length;
      index += LINKED_COUNTS_IDS_CHUNK_SIZE
    ) {
      chunkIdsList.push(ids.slice(index, index + LINKED_COUNTS_IDS_CHUNK_SIZE));
    }

    const responses = await Promise.all(
      chunkIdsList.map((chunkIds) =>
        client.post('/standard-tasks/linked-counts', {
          ids: chunkIds,
        })
      )
    );

    return responses.reduce((acc, response) => {
      Object.assign(acc, response?.data?.byId || {});
      return acc;
    }, {});
  },

  async FETCH_STANDARD_TASK(_, id) {
    const response = await client.get(`/standard-tasks/${id}`);
    return response.data;
  },

  async FETCH_DELETED_STANDARD_TASKS() {
    const response = await client.get('/standard-tasks', {
      params: { deleted: true },
    });
    return response.data || [];
  },

  async CREATE_STANDARD_TASK({ commit }, payload) {
    const response = await client.post('/standard-tasks', payload);
    const standardTask = response.data;
    commit('ADD_STANDARD_TASK', standardTask);
    return standardTask;
  },

  async UPDATE_STANDARD_TASK({ commit }, standardTask) {
    const response = await client.put(
      `/standard-tasks/${standardTask._id}`,
      standardTask
    );
    commit('UPDATE_STANDARD_TASK', response.data);
    return response.data;
  },

  async DELETE_STANDARD_TASK({ commit }, id) {
    await client.delete(`/standard-tasks/${id}`);
    commit('REMOVE_STANDARD_TASK', id);
  },

  async RESTORE_STANDARD_TASK(_, id) {
    const response = await client.put(`/standard-tasks/${id}/restore`);
    return response.data;
  },

  async ADD_STANDARD_TASK_FAVORITE({ dispatch }, { id, count }) {
    const favoriteData = await dispatch(USERS_ADD_FAVORITE, id, {
      root: true,
    });
    if (favoriteData && favoriteData.changed === false) {
      return { status: true, count, changed: false };
    }

    const nextCount = Math.max(0, Number(count) + 1);
    await client.put(`/standard-tasks/${id}`, { favoriteCount: nextCount });
    return { status: true, count: nextCount, changed: true };
  },

  async REMOVE_STANDARD_TASK_FAVORITE(_, { id, count }) {
    const favoriteData = await client.delete(`users/current/favorites/${id}`);
    if (favoriteData.data && favoriteData.data.changed === false) {
      return { status: true, count, changed: false };
    }

    const nextCount = Math.max(0, Number(count) - 1);
    await client.put(`/standard-tasks/${id}`, { favoriteCount: nextCount });
    return { status: true, count: nextCount, changed: true };
  },
};
